// ReportDetailsModal.tsx
import {
  getReportManagementDetails,
  sendWarningEmails,
} from "apis/adminServices/Report&fFeedbackServices";
import React, { useState, useEffect } from "react";
import CreditsSpendIcon from "assets/images/credits-spend.svg"; // Adjust path based on your structure
import CreditsReceivedIcon from "assets/images/credits-received.svg";
import { useToastStore } from "store/toastStore";

interface Report {
  reportManagementId: number;
  requestedBy: string;
  requestedDate: string;
  answeredBy: string;
  answeredDate: string;
  requesterEmail: string;
  responderEmail: string;
  flightNumber: string;
}

interface ReportDetails {
  requesterName: string;
  requesterEmail: string;
  creditsSpent: number;
  requestDate: string;
  responderName: string;
  responderEmail: string;
  creditsReceived: number;
  responseDate: string;
  flightName: string;
  flightNumber: string;
  flightDate: string;
  departureLocation: string;
  destination: string;
  totalFlightDuration: string;
  firstClass: number;
  businessClass: number;
  economyPlusClass: number;
  economyClass: number;
  nonRevListed: number;
  nonFirst: number;
  nonBusiness: number;
  nonEconomyPlus: number;
  nonEconomy: number;
  moreFirstClass: boolean;
  moreBusinessClass: boolean;
  moreEcoClass: boolean;
  moreEcoPlus: boolean;
  comments: string;
}

interface ReportDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: Report | null;
}

const ReportDetailsModal: React.FC<ReportDetailsModalProps> = ({
  isOpen,
  onClose,
  report,
}) => {
  const [reportDetails, setReportDetails] = useState<ReportDetails | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingWarning, setSendingWarning] = useState(false);
  const [warningSent, setWarningSent] = useState(false);
  const { showToast } = useToastStore();

  useEffect(() => {
    const fetchReportDetails = async () => {
      if (!report || !isOpen) return;

      setLoading(true);
      setError(null);
      setWarningSent(false); // Reset warning sent state for new report
      try {
        const details = await getReportManagementDetails(
          report.reportManagementId
        );
        setReportDetails(details);
      } catch (err) {
        console.error("Failed to fetch report details:", err);
        setError("Failed to load report details");
      } finally {
        setLoading(false);
      }
    };

    fetchReportDetails();
  }, [report, isOpen]);

  // Reset warning sent state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setWarningSent(false);
    }
  }, [isOpen]);

  const handleSendWarning = async () => {
    if (!report || sendingWarning || warningSent) return;

    setSendingWarning(true);
    try {
      await sendWarningEmails({
        recipients: [
          {
            name: report.answeredBy,
            email: report.responderEmail,
            flightNumber: report.flightNumber,
          },
        ],
      });
      showToast(
        `Warning email sent to ${report.answeredBy} (${report.responderEmail})`,
        'success'
      );
      setWarningSent(true);
    } catch (error) {
      console.error("Failed to send warning email:", error);
      showToast("Failed to send warning email. Please try again.", 'error');
    } finally {
      setSendingWarning(false);
    }
  };

  if (!isOpen || !report) return null;

  if (loading) {
    return (
      <div className="fixed top-0 right-0 left-0 z-50 overflow-y-auto overflow-x-hidden md:inset-0 justify-center !items-start w-full flex bg-black bg-opacity-50">
        <div className="relative p-4 w-full max-w-xl">
          <div className="relative bg-white rounded-lg shadow pb-4 text-center p-6">
            Loading...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed top-0 right-0 left-0 z-50 overflow-y-auto overflow-x-hidden md:inset-0 justify-center !items-start w-full flex bg-black bg-opacity-50">
        <div className="relative p-4 w-full max-w-xl">
          <div className="relative bg-white rounded-lg shadow pb-4 text-center p-6">
            {error}
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-gray-500 text-white rounded-md"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!reportDetails) return null;

  return (
    <div
      id="default-modal"
      tabIndex={-1}
      aria-hidden={!isOpen}
      className="fixed top-0 right-0 left-0 z-50 overflow-y-auto overflow-x-hidden md:inset-0 justify-center !items-start w-full flex bg-black bg-opacity-50"
    >
      <div className="relative p-4 w-full max-w-xl">
        <div className="relative bg-white rounded-lg shadow pb-4">
          <div className="flex justify-between mx-4 py-4 rounded-t border-b border-lightgray-100">
            <p className="text-base font-semibold text-grease-100">
              Report Details
            </p>
            <button
              type="button"
              className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={onClose}
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          <div className="px-4 py-4">
            <div className="flex items-center justify-between lg:gap-10 gap-4">
              <div className="w-full">
                <div className="flex items-center gap-3">
                  <div className="bg-indigo-500 rounded-md px-2 py-1 text-white text-xs">
                    Requested by
                  </div>
                  <p className="text-xs text-gray-800">
                    {new Date(reportDetails.requestDate).toLocaleString()}
                  </p>
                </div>
                <p className="text-base text-grease-100 mt-2 font-medium">
                  {reportDetails.requesterName}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="bg-gray-400 rounded-full w-6 h-6 flex items-center justify-center">
                    <i className="st-mail text-2xs text-white"></i>
                  </div>
                  <p className="text-xs text-gray-500">
                    {reportDetails.requesterEmail}
                  </p>
                </div>
                <div className="bg-indigo-500 bg-opacity-15 rounded-md border border-gray-300 px-4 py-2 mt-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Credits Spent</p>
                    <p className="text-xl text-grease-100 font-semibold mt-1">
                      {reportDetails.creditsSpent}
                    </p>
                  </div>
                  <img src={CreditsSpendIcon} alt="credits spend" />
                </div>
              </div>
              <div className="w-full">
                <div className="flex items-center gap-3">
                  <div className="bg-sky-500 rounded-md px-2 py-1 text-white text-xs">
                    Answered by
                  </div>
                  <p className="text-xs text-gray-800">
                    {new Date(reportDetails.responseDate).toLocaleString()}
                  </p>
                </div>
                <p className="text-base text-grease-100 mt-2 font-medium">
                  {reportDetails.responderName}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="bg-gray-400 rounded-full w-6 h-6 flex items-center justify-center">
                    <i className="st-mail text-2xs text-white"></i>
                  </div>
                  <p className="text-xs text-gray-500">
                    {reportDetails.responderEmail}
                  </p>
                </div>
                <div className="bg-sky-500 bg-opacity-15 rounded-md border border-gray-300 px-4 py-2 mt-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Credits Received</p>
                    <p className="text-xl text-grease-100 font-semibold mt-1">
                      {reportDetails.creditsReceived}
                    </p>
                  </div>
                  <img src={CreditsReceivedIcon} alt="credits reclvedd" />
                </div>
              </div>
            </div>
            <div className="mt-5 border-t-2 border-gray-100 pt-2">
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center lg:col-span-2 md:col-span-4 col-span-12 gap-3">
                  {/* <img className="w-12" src="images/gulf-air.svg" alt="airline logo" /> */}
                  <div>
                    <p className="text-lightgray-200 text-xs">Airlines</p>
                    <p className="text-grease-100 mt-1 text-sm font-medium">
                      {reportDetails.flightName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center lg:justify-normal md:justify-end lg:col-start-4 lg:col-span-4 md:col-span-8 col-span-12 gap-6">
                  <div>
                    <p className="text-lightgray-200 text-xs">Flight No.</p>
                    <p className="text-grease-100 mt-1 text-sm font-medium">
                      {reportDetails.flightNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-lightgray-200 text-xs">Date</p>
                    <p className="text-grease-100 mt-1 text-sm font-medium">
                      {new Date(reportDetails.flightDate).toLocaleDateString(
                        "en-US",
                        { weekday: "short", month: "short", day: "numeric" }
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-sky-100 bg-opacity-40 rounded-xl p-4 flex items-center lg:col-span-5 md:col-span-12 col-span-12 gap-3 m-4 mb-0">
                <div className="flex flex-col gap-1">
                  <p className="text-grease-100 text-sm font-medium">
                    {reportDetails.departureLocation}
                  </p>
                  <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
                    <i className="st-arrow-slant text-gray-500 font-medium"></i>
                    <p className="text-lightgray-200 text-xs">
                      {new Date(reportDetails.flightDate).toLocaleTimeString(
                        "en-US",
                        { hour: "2-digit", minute: "2-digit" }
                      )}
                    </p>
                  </div>
                </div>
                <div className="w-full">
                  <div className="flex items-center w-full">
                    <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                        <div className="w-1 h-1 bg-royalblue-100 rounded-full"></div>
                      </div>
                    </div>
                    <div className="w-full border-b-2 border-dashed border-indigo-200"></div>
                    <i className="st-flight-slim text-royalblue-100 text-base"></i>
                    <div className="w-full border-b-2 border-dashed border-indigo-200"></div>
                    <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                        <div className="w-1 h-1 bg-royalblue-100 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  <p className="text-lightgray-150 text-xs text-center mt-1">
                    {reportDetails.totalFlightDuration}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <p className="text-grease-100 text-sm font-medium">
                    {reportDetails.destination}
                  </p>
                  <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
                    <i className="st-arrow-slant text-gray-500 font-medium rotate-90"></i>
                    <p className="text-lightgray-200 text-xs">
                      {new Date(
                        new Date(reportDetails.flightDate).getTime() +
                          parseInt(
                            reportDetails.totalFlightDuration.split(":")[0]
                          ) *
                            3600000 +
                          parseInt(
                            reportDetails.totalFlightDuration.split(":")[1]
                          ) *
                            60000 +
                          parseInt(
                            reportDetails.totalFlightDuration.split(":")[2]
                          ) *
                            1000
                      ).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 p-4">
                <div className="flex-1 text-center h-14 content-center border border-lightgray-100 rounded-md">
                  <p className="text-grease-100 font-medium text-sm">
                    {reportDetails.firstClass}
                  </p>
                  <p className="text-lightgray-150 text-xs">First</p>
                </div>
                <div className="flex-1 text-center h-14 content-center border border-lightgray-100 rounded-md">
                  <p className="text-grease-100 font-medium text-sm">
                    {reportDetails.economyClass}
                  </p>
                  <p className="text-lightgray-150 text-xs">Eco</p>
                </div>
                <div className="flex-1 text-center h-14 content-center border border-lightgray-100 rounded-md">
                  <p className="text-grease-100 font-medium text-sm">
                    {reportDetails.economyPlusClass}
                  </p>
                  <p className="text-lightgray-150 text-xs">Eco+</p>
                </div>
                <div className="flex-1 text-center h-14 content-center border border-lightgray-100 rounded-md">
                  <p className="text-grease-100 font-medium text-sm">
                    {reportDetails.businessClass}
                  </p>
                  <p className="text-lightgray-150 text-xs">Bus</p>
                </div>
              </div>
              <div className="flex flex-col items-center justify-between gap-2 mb-7 mt-3">
                <p className="font-medium text-sm text-grease-100">
                  Listed Passengers
                </p>
                <div className="px-5 text-center h-14 content-center border border-lightgray-100 rounded-md">
                  <p className="text-grease-100 font-medium text-sm">
                    {reportDetails.nonRevListed}
                  </p>
                  <p className="text-lightgray-150 text-xs">Non Rev</p>
                </div>
              </div>
              <div className="border border-blue-100 bg-white rounded-xl p-4 m-4">
                <div className="py-4 border-b border-blue-100">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportDetails.moreFirstClass}
                      readOnly
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-blue-100 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ms-3 text-sm font-thin text-lightgray-200">
                      Overbooked
                    </span>
                  </label>
                </div>
                <div className="py-4 border-b border-blue-100">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportDetails.moreBusinessClass}
                      readOnly
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-blue-100 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ms-3 text-sm font-thin text-lightgray-200">
                      This flight has no First class
                    </span>
                  </label>
                </div>
                <div className="pt-4">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reportDetails.moreEcoClass}
                      readOnly
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-blue-100 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ms-3 text-sm font-thin text-lightgray-200">
                      There are more seats available in First class but I don’t
                      know how many
                    </span>
                  </label>
                </div>
              </div>
              <div>
                <label className="text-xs font-light text-gray-500">
                  Comments
                </label>
                <input
                  className="bg-blue-50 text-gray-600 text-xs rounded-md h-20 block w-full p-2.5 mt-2"
                  value={reportDetails.comments || ""}
                  readOnly
                />
              </div>
            </div>
            <div className="flex gap-4 items-center justify-center px-4 mt-2">
              <button
                onClick={handleSendWarning}
                disabled={sendingWarning || warningSent}
                className={`flex items-center gap-2 py-3 px-6 bg-amber-500 rounded-md transition-opacity ${
                  (sendingWarning || warningSent) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-amber-600'
                }`}
              >
                {sendingWarning ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="font-medium text-white text-xs">Sending Warning...</p>
                  </>
                ) : warningSent ? (
                  <>
                    <svg className="w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="font-medium text-white text-xs">Warning Sent</p>
                  </>
                ) : (
                  <>
                    <i className="st-warning text-xs text-white"></i>
                    <p className="font-medium text-white text-xs">Send Warning Email</p>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportDetailsModal;
